import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { ShieldX, Download, ChevronRight, X, Shield } from "lucide-react";

export function CertBanner() {
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const [downloadError, setDownloadError] = useState(false);

  const getOS = (): "windows" | "mac" | "linux" => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("windows")) return "windows";
    if (ua.includes("mac")) return "mac";
    return "linux";
  };
  const os = getOS();

  // nginx 가 /rootCA.pem 만 서빙함 → 받아서 .crt 이름으로 저장 (Windows 더블클릭 설치용)
  // 상대경로라 포트가 달라도 같은 origin 에서 받음
  const handleDownload = async () => {
    setDownloadError(false);
    try {
      const res = await fetch("/rootCA.pem", { cache: "no-store" });
      const text = await res.text();
      // 없는 경로면 SPA index.html 이 오므로 내용으로 검증
      if (!res.ok || !text.startsWith("-----BEGIN CERTIFICATE-----"))
        throw new Error("invalid cert");
      const url = URL.createObjectURL(
        new Blob([text], { type: "application/x-x509-ca-cert" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "rootCA.crt";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setDownloadError(true);
    }
  };

  const Step = ({
    num,
    title,
    children,
  }: {
    num: number;
    title: string;
    children: React.ReactNode;
  }) => (
    <Box
      sx={{
        display: "flex",
        gap: 1.75,
        py: 1.25,
        "&+&": { borderTop: "1px solid", borderColor: "divider" },
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 500,
          color: "text.secondary",
          flexShrink: 0,
          mt: 0.2,
        }}
      >
        {num}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.4 }}>
          {title}
        </Typography>
        <Typography
          component="div"
          sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.65 }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );

  const PathPill = ({ steps }: { steps: string[] }) => (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1,
        py: 0.5,
        mt: 0.75,
      }}
    >
      {steps.map((s, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
            {s}
          </Typography>
          {i < steps.length - 1 && (
            <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
              ›
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Alert
        severity="warning"
        icon={<ShieldX size={18} />}
        action={
          <Button
            size="small"
            variant="contained"
            color="warning"
            startIcon={<Shield size={14} />}
            onClick={() => setOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            인증서 설치 안내
          </Button>
        }
        sx={{ mb: 2, alignItems: "center" }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
          서비스 워커가 활성화되지 않았습니다
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
          보안 인증서(rootCA)를 설치해야 정상 작동합니다
        </Typography>
      </Alert>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Shield size={18} />
            <Typography fontWeight={500}>
              보안 인증서(rootCA) 설치 안내
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2.5 }}
        >
          {/* 다운로드 */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "text.disabled",
                mb: 1.25,
              }}
            >
              1단계 — 인증서 다운로드
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: "info.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Shield size={18} color="#1976d2" />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    rootCA.crt
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    HAIF 로컬 루트 인증서 · X.509
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="info"
                startIcon={<Download size={13} />}
                onClick={handleDownload}
              >
                다운로드
              </Button>
            </Box>
            {downloadError && (
              <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>
                인증서 파일을 가져오지 못했습니다. 관리자에게 문의해 주세요.
              </Alert>
            )}
          </Box>

          {/* 설치 단계 */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "text.disabled",
                mb: 1,
              }}
            >
              2단계 — 인증서 설치 (
              {os === "windows" ? "Windows" : os === "mac" ? "macOS" : "Linux"})
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {os === "windows" && (
                <>
                  <Step num={1} title="인증서 파일 열기">
                    다운로드한{" "}
                    <Chip
                      label="rootCA.crt"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    를 더블클릭 →{" "}
                    <Chip
                      label="인증서 설치"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    를 클릭합니다.
                  </Step>
                  <Step num={2} title="저장소 위치 선택">
                    <Chip
                      label="로컬 컴퓨터"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    선택 후 다음 (관리자 권한 확인창은 예)
                  </Step>
                  <Step num={3} title="루트 인증 기관에 저장">
                    <Chip
                      label="모든 인증서를 다음 저장소에 저장"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    →{" "}
                    <Chip
                      label="찾아보기"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    →{" "}
                    <Chip
                      label="신뢰할 수 있는 루트 인증 기관"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    선택 후 다음 → 마침
                  </Step>
                </>
              )}
              {os === "mac" && (
                <>
                  <Step num={1} title="키체인에 추가">
                    다운로드한{" "}
                    <Chip
                      label="rootCA.crt"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    를 더블클릭하여{" "}
                    <Chip
                      label="시스템"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    키체인에 추가합니다.
                  </Step>
                  <Step num={2} title="항상 신뢰로 변경">
                    키체인 접근에서 추가된 인증서(mkcert ...)를 더블클릭합니다.
                    <PathPill
                      steps={["신뢰", "이 인증서 사용 시", "항상 신뢰"]}
                    />
                  </Step>
                  <Step num={3} title="저장">
                    창을 닫고 Mac 암호를 입력하면 적용됩니다.
                  </Step>
                </>
              )}
              {os === "linux" && (
                <>
                  <Step num={1} title="크롬 인증서 관리 열기">
                    주소창에 아래 주소를 직접 입력합니다.
                    <PathPill steps={["chrome://certificate-manager"]} />
                  </Step>
                  <Step num={2} title="가져오기">
                    <PathPill
                      steps={[
                        "로컬 인증서",
                        "사용자 설정",
                        "신뢰할 수 있는 인증서",
                        "가져오기",
                      ]}
                    />
                  </Step>
                  <Step num={3} title="파일 선택">
                    다운로드한{" "}
                    <Chip
                      label="rootCA.crt"
                      size="small"
                      sx={{ fontSize: 11, height: 18, mx: 0.3 }}
                    />{" "}
                    를 선택합니다.
                  </Step>
                </>
              )}
              <Step num={4} title="브라우저 재시작">
                브라우저 창을 모두 닫고 다시 실행한 뒤 이 페이지를 새로고침하면
                SW 상태가{" "}
                <Chip
                  label="activated"
                  color="success"
                  size="small"
                  sx={{ fontSize: 11, height: 18, ml: 0.3 }}
                />{" "}
                로 바뀝니다.
              </Step>
            </Box>
          </Box>

          {/* 수동 설치 토글 */}
          <Box>
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={
                <ChevronRight
                  size={13}
                  style={{
                    transition: "transform 0.18s",
                    transform: manualOpen ? "rotate(90deg)" : "rotate(0)",
                  }}
                />
              }
              onClick={() => setManualOpen((p) => !p)}
              sx={{ fontSize: 12, color: "text.secondary", px: 0 }}
            >
              설치가 안 되나요? 다른 방법 보기
            </Button>
            <Collapse in={manualOpen}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 1 }}>
                  Windows — MMC 콘솔
                </Typography>
                <PathPill
                  steps={[
                    "Win+R → mmc",
                    "파일 › 스냅인 추가",
                    "인증서",
                    "컴퓨터 계정",
                    "신뢰할 수 있는 루트 인증 기관",
                    "가져오기",
                  ]}
                />
                <Typography
                  sx={{ fontSize: 12, fontWeight: 500, mt: 2, mb: 1 }}
                >
                  Chrome / Edge 자체 인증서 관리
                </Typography>
                <PathPill
                  steps={[
                    "chrome://certificate-manager",
                    "로컬 인증서",
                    "사용자 설정",
                    "가져오기",
                  ]}
                />
                <Typography
                  sx={{ fontSize: 12, fontWeight: 500, mt: 2, mb: 1 }}
                >
                  Firefox
                </Typography>
                <PathPill
                  steps={[
                    "설정",
                    "개인 정보 및 보안",
                    "인증서 보기",
                    "인증 기관",
                    "가져오기",
                    "웹사이트 식별 신뢰 체크",
                  ]}
                />
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
